import {RawAxiosRequestConfig} from "axios";
import {ApiConfig} from "components/src/api/portal/ApiConfig";
import {AuthApi, PortalApi, SystemApi} from "components/src/api/portal/generated";
import {AuthenticatedRoute} from "components/src/auth/AuthenticatedRoute";
import {PortalForgotPassword} from "components/src/auth/PortalForgotPassword";
import {PortalLogin} from "components/src/auth/PortalLogin";
import {PortalPasswordReset} from "components/src/auth/PortalPasswordReset";
import {PortalSignup} from "components/src/auth/PortalSignup";
import {Success} from "components/src/auth/Success";
import {Tfa} from "components/src/auth/Tfa";
import {TfaSetupRequired} from "components/src/auth/TfaSetupRequired";
import {ErrorPage} from "components/src/common/ErrorPage";
import {Loading} from "components/src/common/Loading";
import {AuthenticatedUserContextProvider} from "components/src/context/AuthenticatedUserContextProvider";
import {SpinnerStatusContextProvider} from "components/src/context/SpinnerStatusContextProvider";
import {PortalDashboard} from "components/src/dashboard/PortalDashboard";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {AppLayout} from "components/src/layout/AppLayout";
import {LoginLayout} from "components/src/layout/LoginLayout";
import {PDFGeneratePassword} from "components/src/portal/PDFGeneratePassword";
import {PDFReply} from "components/src/portal/PDFReply";
import React, {ComponentType, Suspense} from 'react'
import {createPortal} from "react-dom";
import {createBrowserRouter, createRoutesFromElements, Route, RouterProvider} from "react-router-dom";
import {PortalAppMenu} from "./PortalAppMenu";

interface AppProps {
    Sidebar : ComponentType<{
        className?: string;
    }>
    OptionalSidePanel? : ComponentType<{
        className?: string;
    }>
    children? : React.ReactNode;
    AuthenticatedWrapper? : ComponentType<{children: React.ReactNode}>;
    DashboardBeforeContent? : React.ReactNode;
}

/**
 * REST api for accessing the back-end
 */
const authApi = new AuthApi(ApiConfig.config());
const systemApi = new SystemApi(ApiConfig.config());
const portalApi = new PortalApi(ApiConfig.config());

export const PortalApp = ({Sidebar, OptionalSidePanel, children, AuthenticatedWrapper, DashboardBeforeContent} : AppProps) =>
{
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    const validateOTPFunc = async (totp : string, options?: RawAxiosRequestConfig) => {
        return await authApi.authControllerValidateTOTP({otp : totp}, options);
    };

    const router = createBrowserRouter(
        createRoutesFromElements(
            <>
                {/* some pages should be accessible without authentication */}
                <Route path="/" errorElement={<ErrorPage/>} element={
                    <LoginLayout/>
                }>
                    <Route path="login" element={
                        <PortalLogin/>
                    } />
                    <Route path="forgot-password" element={
                        <PortalForgotPassword forgotPassword={(email) => authApi.authControllerForgotPassword(email)}/>
                    } />
                    <Route path="password-reset" element={
                        <PortalPasswordReset
                            validatePortalPasswordReset={(resetCode) => authApi.authControllerValidatePortalPasswordReset(resetCode)}
                            resetPortalPassword={(resetCode, portalPasswordAndQuestions) => authApi.authControllerResetPortalPassword(resetCode, portalPasswordAndQuestions)}
                        />
                    } />
                    <Route path="signup" element={
                        <PortalSignup/>
                    } />
                </Route>
                <Route path={"tfa"} element={
                    <Tfa validateOTPFunc={validateOTPFunc}/>
                } />
                <Route path={"success"} element={
                    <Success/>
                } />
                <Route path="pdf/reply" element={
                    <PDFReply/>
                } />

                <Route path="/" element={
                    // all child "pages" (except login) will be wrapped by the following context providers
                    // and will have the AdminLayout as the parent component
                    // because the AdminLayout is wrapped with an AuthenticatedRoute, all access to the admin
                    // pages requires authentication
                    <SpinnerStatusContextProvider>
                        <AuthenticatedUserContextProvider>
                            <AuthenticatedRoute>
                                <TfaSetupRequired
                                    fetchTfaProperties={() => portalApi.portalControllerGetPortalProperties().then(r => ({
                                        tfaRequired: r.data.tfaRequired ?? false,
                                        tfaEnabled: r.data.tfaEnabled ?? false
                                    }))}
                                    is2FAEnabledFunc={() => portalApi.portalControllerGetPortalProperties().then(r => ({
                                        ...r,
                                        data: r.data.tfaEnabled ?? false
                                    }))}
                                    generateSecretFunc={() => authApi.authControllerGenerateTFASecret()}
                                    createSecretQRCodeFunc={(secret: string) => authApi.authControllerCreate2FASecretQRCode(secret)}
                                    set2FAEnabledFunc={(enabled: boolean) => authApi.authControllerSet2FAEnabled(enabled)}
                                    validateOTPFunc={(otpCode: string, secret: string) => authApi.authControllerValidateTOTP({otp: otpCode, tfaSecret: secret})}
                                    setSecretFunc={(secret: string) => authApi.authControllerSet2FASecret(secret)}
                                >
                                    {AuthenticatedWrapper
                                        ? <AuthenticatedWrapper>
                                            <AppLayout AppSidebar={Sidebar}
                                                       Menu={PortalAppMenu}
                                                       OptionalSidePanel={OptionalSidePanel}
                                                       fetchSessionTimeout={() => systemApi.systemControllerGetSessionTimeout().then(r => r.data * 1000)}
                                                       keepSessionAlive={() => systemApi.systemControllerGetSessionTimeout().catch(() => {})}/>
                                          </AuthenticatedWrapper>
                                        : <AppLayout AppSidebar={Sidebar}
                                                     Menu={PortalAppMenu}
                                                     OptionalSidePanel={OptionalSidePanel}
                                                     fetchSessionTimeout={() => systemApi.systemControllerGetSessionTimeout().then(r => r.data * 1000)}
                                                     keepSessionAlive={() => systemApi.systemControllerGetSessionTimeout().catch(() => {})}/>
                                    }
                                </TfaSetupRequired>
                            </AuthenticatedRoute>
                        </AuthenticatedUserContextProvider>
                    </SpinnerStatusContextProvider>
                }>
                    {/* The below child routes are wrapped by the AdminLayout and require authentication */}
                    <Route index element={
                        <PortalDashboard beforeContent={DashboardBeforeContent}/>
                    } />
                    <Route path={"pdf/otp"} element={
                        <PDFGeneratePassword/>
                    } />
                    {children}
                </Route>
            </>
        )
    );

    return (
        <React.StrictMode>
            {createPortal(
                <>
                    <link rel="icon" href={t(["favicon"])} />
                    <title>{t(["portal.title"])}</title>
                </>,
                document.head
            )}

            <Suspense fallback={<Loading/>}>
                <RouterProvider router={router} />
            </Suspense>
        </React.StrictMode>
    )
}
